"use client";

import React, { useState } from "react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Badge } from "@/app/_module/components/ui/badge";
import { MapPin, Loader2, Navigation } from "lucide-react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import {
  useAgencyRouteSettings,
  useUpdateAgencyRouteSettings,
} from "../../hooks/use-agency-routes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";

interface RoutesToggleProps {
  agencyId: string;
}

export default function RoutesToggle({ agencyId }: RoutesToggleProps) {
  const { user } = useUserContext();
  const [showConfirm, setShowConfirm] = useState(false);

  const role = user?.currentAgency?.role?.toLowerCase?.() || "";
  const isOwnerOrAdmin = ["owner", "admin", "agency_owner"].includes(role);

  const { data, isLoading } = useAgencyRouteSettings(agencyId);
  const { mutate: updateSettings, isPending: saving } =
    useUpdateAgencyRouteSettings(agencyId);

  const enabled = data?.routesEnabled === true;

  const handleToggleRequest = (value: boolean) => {
    if (value) {
      setShowConfirm(true);
    } else {
      updateSettings({ enabled: false });
    }
  };

  const handleConfirmEnable = () => {
    updateSettings({ enabled: true });
    setShowConfirm(false);
  };

  if (!isOwnerOrAdmin) return null;

  return (
    <>
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Enable Routes Tracking
                  </h3>
                  {enabled && (
                    <Badge
                      variant="secondary"
                      className="bg-emerald-50 text-emerald-700 text-[10px] font-medium"
                    >
                      Active
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                  Track agent door-knocking routes and see live agent positions in
                  Agency Center → Routes.
                </p>
                {enabled && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Navigation className="w-3 h-3 text-emerald-500" />
                    <span className="text-[11px] text-emerald-600 font-medium">
                      Live agent positions and agency-wide route stats are now visible.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="shrink-0 pt-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
              ) : (
                <Switch
                  checked={enabled}
                  onCheckedChange={handleToggleRequest}
                  disabled={saving}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <AlertDialogTitle>Enable Routes Tracking?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                Agency owners and admins will see live agent locations during active
                routes and agency-wide route stats on the dashboard.
              </span>
              <span className="block text-gray-500 text-xs">
                You can disable this at any time.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleConfirmEnable}
            >
              Enable Tracking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
